/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { PolygonLayer, TextLayer } from '@deck.gl/layers';
import {
  booleanIntersects, centroid, hexGrid,
} from '@turf/turf';

import Map, {
  MapLegend,
} from '@App/components/Map/Map';
import festivalData from '@App/components/Map/data/festival-data';
import flandersBrusselsRegion from '@App/components/Map/data/flanders-brussels-region';
import Question4Form from '@App/pages/Tablet/Question4/Question4Form';
import {
  getCount,
} from '../question.utils';

const QUESTION_ID = 'question4';
const HEXAGON_COLORS = [
  [25, 56, 92],
  [28, 117, 126],
  [87, 177, 133],
  [228, 195, 91],
  [220, 105, 73],
];
const HEXAGON_BOUNDS = [2.5, 50.65, 5.9, 51.55];
const HEXAGON_SIDE_KM = 2.8;
const MIN_HEXAGON_ELEVATION = 360;
const MAX_HEXAGON_ELEVATION = 18000;
const BASE_HEXAGONS = hexGrid(HEXAGON_BOUNDS, HEXAGON_SIDE_KM, {
  units: 'kilometers',
}).features
  .filter(feature => flandersBrusselsRegion.some(region => booleanIntersects(feature, region)))
  .map(feature => ({
    polygon: feature.geometry.coordinates[0],
    coordinates: centroid(feature).geometry.coordinates,
    dancePhase: Math.random() * Math.PI * 2,
    danceSpeed: 0.65 + (Math.random() * 0.8),
  }));
function getNearestHexagonIndex(coordinates) {
  if (!coordinates) return -1;

  return BASE_HEXAGONS.reduce((nearestIndex, hexagon, index) => {
    const nearest = BASE_HEXAGONS[nearestIndex];
    const distance = (hexagon.coordinates[0] - coordinates[0]) ** 2
      + (hexagon.coordinates[1] - coordinates[1]) ** 2;
    const nearestDistance = (nearest.coordinates[0] - coordinates[0]) ** 2
      + (nearest.coordinates[1] - coordinates[1]) ** 2;
    return distance < nearestDistance ? index : nearestIndex;
  }, 0);
}

function getAnswerColor(answerCount, maxAnswerCount) {
  const weight = maxAnswerCount > 0 ? answerCount / maxAnswerCount : 0;
  const position = weight * (HEXAGON_COLORS.length - 1);
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.min(lowerIndex + 1, HEXAGON_COLORS.length - 1);
  const factor = position - lowerIndex;

  return HEXAGON_COLORS[lowerIndex].map((color, index) => Math.round(
    color + ((HEXAGON_COLORS[upperIndex][index] - color) * factor),
  ));
}

function buildQuestionData(answerCount, lastAnswer) {
  const answer = lastAnswer?.questionId === QUESTION_ID ? lastAnswer.answer : null;
  const selectedCount = answer
    ? getCount(answerCount, [answer.value, answer.label])
    : 0;
  const selectedIndex = getNearestHexagonIndex(answer?.coordinates);
  const hexagonCounts = festivalData.features.reduce((counts, feature) => {
    const coordinates = feature.geometry?.coordinates;
    const hexagonIndex = getNearestHexagonIndex(coordinates);
    const count = getCount(answerCount, [
      feature.properties?.id,
      feature.properties?.name,
    ]);

    if (hexagonIndex >= 0) counts[hexagonIndex] += count;
    return counts;
  }, BASE_HEXAGONS.map(() => 0));
  const maxAnswerCount = Math.max(...hexagonCounts, 1);

  return BASE_HEXAGONS.map((hexagon, index) => ({
    ...hexagon,
    answerCount: hexagonCounts[index],
    maxAnswerCount,
    selected: index === selectedIndex,
    selectedCount: index === selectedIndex ? selectedCount : 0,
  }));
}

function getQuestionLayers({
  data,
  lastAnswer,
  options: { animationTime = 0 } = {},
}) {
  const animatedData = data.map(item => ({
    ...item,
    heightWeight: (Math.sin(
      (animationTime * item.danceSpeed) + item.dancePhase,
    ) + 1) / 2,
  }));
  const getElevation = (item) => {
    const popularityWeight = item.maxAnswerCount > 0
      ? item.answerCount / item.maxAnswerCount
      : 0;
    const popularityElevation = MIN_HEXAGON_ELEVATION
      + (popularityWeight * (MAX_HEXAGON_ELEVATION - MIN_HEXAGON_ELEVATION));
    const selectedBonus = item.selected
      ? 1800 + (item.selectedCount * 360)
      : 0;
    const baseElevation = popularityElevation + selectedBonus;
    const danceScale = 0.75 + (item.heightWeight * 0.5);

    return baseElevation * danceScale;
  };
  const answer = lastAnswer?.questionId === QUESTION_ID ? lastAnswer.answer : null;
  const selectedHexagon = animatedData.find(item => item.selected);
  const labelElevation = selectedHexagon
    ? getElevation(selectedHexagon) + 250
    : 0;
  const selectedAnswer = answer?.coordinates
    ? [{
      coordinates: answer.coordinates,
      elevation: labelElevation,
      name: answer.label || answer.value,
    }]
    : [];

  return [new PolygonLayer({
    id: 'question-4-hexagons',
    data: animatedData,
    getElevation,
    getFillColor: item => [
      ...getAnswerColor(item.answerCount, item.maxAnswerCount),
      item.selected ? 240 : 190,
    ],
    getLineColor: [20, 31, 46, 100],
    getLineWidth: 1,
    getPolygon: item => item.polygon,
    extruded: true,
    pickable: false,
    stroked: true,
  }), new TextLayer({
    id: 'question-4-answer-label',
    data: selectedAnswer,
    getColor: [255, 255, 255, 235],
    getPosition: item => [...item.coordinates, item.elevation],
    getSize: 14,
    getText: item => item.name,
    getPixelOffset: [0, -14],
    getTextAnchor: 'middle',
    getAlignmentBaseline: 'bottom',
    outlineColor: [20, 31, 46, 255],
    outlineWidth: 3,
    pickable: false,
  })];
}

function Question4(props) {
  const { withInputFields = false, ...visualizationProps } = props;
  const [animationTime, setAnimationTime] = useState(0);
  const { lastAnswer: contextLastAnswer } = useOutletContext() || {};
  const lastAnswer = props.lastAnswer ?? contextLastAnswer;
  const focusCoordinates = lastAnswer?.questionId === QUESTION_ID
    ? lastAnswer.answer?.coordinates || null
    : null;

  useEffect(() => {
    let animationFrame;
    const animate = (timestamp) => {
      setAnimationTime(timestamp / 1000);
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, []);

  return (
    <Map
      questionId={QUESTION_ID}
      buildData={buildQuestionData}
      getLayers={getQuestionLayers}
      autoRotate
      focusCoordinates={focusCoordinates}
      focusZoom={10.5}
      returnToOverview
      layerOptions={{ animationTime }}
      mapOptions={{ pitch: 45 }}
      legend={(
        <MapLegend
          lang={visualizationProps.tabletLang}
          labelKey="legend-vote-count"
          gradient="linear-gradient(90deg, #19385c, #1c757e, #57b185, #e4c35b, #dc6949)"
          maxLabelKey="legend-high"
        />
      )}
      {...visualizationProps}
    >
      {withInputFields && <Question4Form />}
    </Map>
  );
}

export default Question4;
